import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from "@mui/material";

import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import * as htmlToImage from "html-to-image";

import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DownloadIcon from "@mui/icons-material/Download";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BarChartIcon from "@mui/icons-material/BarChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import InfoIcon from "@mui/icons-material/Info";
import RadarIcon from "@mui/icons-material/Radar";
import InsightsIcon from "@mui/icons-material/Insights";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { getAuthToken } from "../utils/session";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import RadarChartComponent from "../components/RadarChartComponent";
import DetallePorProceso from "../components/DetallePorProceso";
import GeneradorPDF from "../components/GeneradorPDF";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const FONT_FAMILY =
  'Inter, Manrope, "SF Pro Display", "Segoe UI", Roboto, Arial, sans-serif';

const getTheme = (mode) => {
  const isDark = mode === "dark";

  return {
    mode,

    page: isDark ? "#07111F" : "#F7F9FC",
    pageSoft: isDark ? "#0B1728" : "#FFFFFF",
    card: isDark ? "rgba(255,255,255,0.065)" : "#FFFFFF",
    cardSoft: isDark ? "rgba(255,255,255,0.045)" : "#FBFCFE",
    elevated: isDark ? "rgba(255,255,255,0.085)" : "#FFFFFF",

    text: isDark ? "#F8FAFC" : "#172033",
    heading: isDark ? "#FFFFFF" : "#111827",
    muted: isDark ? "#91A4BC" : "#64748B",

    blue: isDark ? "#4F9CF9" : "#1E4B81",
    blueDark: isDark ? "#3E8BE5" : "#14365A",
    orange: "#D37D36",
    orangeDark: "#B6632C",
    success: isDark ? "#22C55E" : "#15803D",
    slate: isDark ? "#CBD5E1" : "#475569",
    purple: isDark ? "#A78BFA" : "#5B5CE2",
    danger: isDark ? "#FB7185" : "#D32F2F",

    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,36,56,0.10)",
    borderSoft: isDark ? "rgba(148,163,184,0.10)" : "rgba(15,36,56,0.07)",
    borderStrong: isDark ? "rgba(148,163,184,0.26)" : "rgba(15,36,56,0.16)",

    shadow: isDark
      ? "0 24px 60px rgba(0,0,0,0.26)"
      : "0 18px 48px rgba(15,36,56,0.06)",

    hoverShadow: isDark
      ? "0 18px 42px rgba(0,0,0,0.28)"
      : "0 18px 42px rgba(15,36,56,0.08)",

    background: isDark
      ? "radial-gradient(circle at 18% 0%, rgba(79,156,249,0.16), transparent 32%), radial-gradient(circle at 92% 8%, rgba(211,125,54,0.10), transparent 28%), linear-gradient(180deg, #07111F 0%, #0B1728 48%, #07111F 100%)"
      : "radial-gradient(circle at 18% 0%, rgba(30,75,129,0.08), transparent 32%), radial-gradient(circle at 92% 8%, rgba(211,125,54,0.08), transparent 28%), linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 48%, #F7F9FC 100%)",
  };
};

const getPanelSx = (C) => ({
  bgcolor: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: "22px",
  boxShadow: C.shadow,
  backdropFilter: C.mode === "dark" ? "blur(18px)" : "none",
});

const abreviaturasProcesos = {
  "Establecimiento de la estrategia del dato": "EstDat",
  "Establecimiento de políticas, buenas prácticas y procedimientos del dato": "PolDat",
  "Establecimiento de estructuras organizativas para el gobierno, gestión y uso del dato": "EstOrg",
  "Optimización de los riesgos del dato": "RiesDat",
  "Optimización del valor del dato": "ValDat",
  "Planificación de la calidad del dato": "PlanDQ",
  "Control y monitorización de la calidad del dato": "CtrlDQ",
  "Aseguramiento de la calidad del dato": "AsegDQ",
  "Mejora de la calidad del dato": "MejDQ",
  "Procesamiento del dato": "ProcDat",
  "Gestión de infraestructura tecnológica del dato": "InfraTec",
  "Gestión de requisitos del dato": "ReqDat",
  "Gestión de seguridad del dato": "SegDat",
  "Gestión del metadato": "MetDat",
  "Gestión de la arquitectura y diseño del dato": "ArqDat",
  "Gestión de configuración del dato": "ConfDat",
  "Compartición, intermediación e integración del dato": "CIIDat",
  "Gestión del ciclo de vida del dato": "CVidDat",
  "Gestión del dato histórico": "DatHis",
  "Gestión del dato maestro": "MDM",
  "Gestión de recursos humanos": "RRHH",
  "Análisis del dato": "AnaDat",
};

const ordenDeseado = [
  "ProcDat",
  "InfraTec",
  "ReqDat",
  "ConfDat",
  "DatHis",
  "SegDat",
  "MetDat",
  "CtrlDQ",
  "PolDat",
  "ArqDat",
  "CIIDat",
  "MDM",
  "RRHH",
  "CVidDat",
  "AnaDat",
  "PlanDQ",
  "EstDat",
  "EstOrg",
  "RiesDat",
  "AsegDQ",
  "ValDat",
  "MejDQ",
];

const procesosPorTipo = {
  Gobierno: ["PolDat", "ValDat", "RiesDat", "EstOrg", "EstDat"],
  Gestion: [
    "ProcDat",
    "AnaDat",
    "InfraTec",
    "CVidDat",
    "ReqDat",
    "ConfDat",
    "RRHH",
    "MDM",
    "DatHis",
    "CIIDat",
    "SegDat",
    "ArqDat",
    "MetDat",
  ],
  Calidad: ["CtrlDQ", "AsegDQ", "PlanDQ", "MejDQ"],
};

const SectionHeader = ({ eyebrow, title, description, action, C }) => (
  <Stack
    direction={{ xs: "column", md: "row" }}
    alignItems={{ xs: "flex-start", md: "flex-end" }}
    justifyContent="space-between"
    gap={1.5}
    sx={{ mb: 2 }}
  >
    <Box>
      {eyebrow && (
        <Typography
          sx={{
            color: C.blue,
            fontWeight: 850,
            fontSize: "0.64rem",
            letterSpacing: "0.11em",
            textTransform: "uppercase",
            mb: 0.45,
            fontFamily: FONT_FAMILY,
          }}
        >
          {eyebrow}
        </Typography>
      )}

      <Typography
        sx={{
          color: C.heading,
          fontWeight: 900,
          fontSize: { xs: "1rem", md: "1.15rem" },
          letterSpacing: "-0.035em",
          lineHeight: 1.2,
          fontFamily: FONT_FAMILY,
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          sx={{
            color: C.muted,
            fontSize: "0.78rem",
            lineHeight: 1.6,
            maxWidth: 760,
            mt: 0.55,
            fontFamily: FONT_FAMILY,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>

    {action}
  </Stack>
);

const StatCard = ({
  icon,
  label,
  value,
  title,
  subtitle,
  footer,
  accent,
  children,
  C,
}) => (
  <Paper
    elevation={0}
    sx={{
      ...getPanelSx(C),
      p: 1.9,
      minHeight: 164,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      transition: "all 200ms ease",

      "&:hover": {
        transform: { xs: "none", md: "translateY(-3px)" },
        boxShadow: C.hoverShadow,
        borderColor: alpha(accent, 0.35),
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "14px",
          bgcolor: alpha(accent, C.mode === "dark" ? 0.16 : 0.08),
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${alpha(accent, 0.18)}`,
          "& svg": { fontSize: 20 },
        }}
      >
        {icon}
      </Box>

      {label && (
        <Chip
          size="small"
          label={label}
          sx={{
            height: 22,
            fontSize: "0.66rem",
            fontWeight: 800,
            bgcolor: C.cardSoft,
            color: C.text,
            border: `1px solid ${C.borderSoft}`,
            fontFamily: FONT_FAMILY,
          }}
        />
      )}
    </Stack>

    <Box sx={{ mt: 1.8 }}>
      {children || (
        <>
          <Typography
            sx={{
              color: C.heading,
              fontSize: "1.55rem",
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: "-0.055em",
              fontFamily: FONT_FAMILY,
            }}
          >
            {value}
          </Typography>

          <Typography
            sx={{
              color: C.text,
              fontSize: "0.78rem",
              fontWeight: 750,
              mt: 0.75,
              fontFamily: FONT_FAMILY,
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              sx={{
                color: C.muted,
                fontSize: "0.7rem",
                mt: 0.25,
                fontFamily: FONT_FAMILY,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </>
      )}
    </Box>

    {footer && <Box sx={{ mt: 1.4 }}>{footer}</Box>}
  </Paper>
);

const ClassicButton = ({ children, sx, C, ...props }) => (
  <Button
    {...props}
    variant="contained"
    sx={{
      bgcolor: C.blue,
      color: "#fff",
      borderRadius: "13px",
      px: 1.8,
      py: 0.85,
      fontSize: "0.76rem",
      fontWeight: 800,
      textTransform: "none",
      boxShadow:
        C.mode === "dark"
          ? "0 14px 28px rgba(79,156,249,0.18)"
          : "0 14px 28px rgba(30,75,129,0.14)",
      fontFamily: FONT_FAMILY,

      "&:hover": {
        bgcolor: C.blueDark,
        boxShadow: "none",
      },

      ...sx,
    }}
  >
    {children}
  </Button>
);

const DashboardSkeleton = ({ C }) => (
  <Box
    sx={{
      minHeight: "100vh",
      background: C.background,
      p: { xs: 2, md: 3 },
      fontFamily: FONT_FAMILY,
    }}
  >
    <Skeleton variant="rounded" height={52} sx={{ borderRadius: 3, mb: 2 }} />
    <Skeleton variant="rounded" height={190} sx={{ borderRadius: 3, mb: 2 }} />

    <Grid container spacing={2} sx={{ mb: 2 }}>
      {[...Array(4)].map((_, idx) => (
        <Grid item xs={12} md={3} key={idx}>
          <Skeleton variant="rounded" height={168} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>

    <Grid container spacing={2}>
      {[...Array(3)].map((_, idx) => (
        <Grid item xs={12} md={4} key={idx}>
          <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

const BarChartCapacidad = ({ resultadosProceso, userRole, C }) => {
  const procesosOrdenados = useMemo(() => {
    return [...(resultadosProceso || [])].sort((a, b) => {
      const abrevA = abreviaturasProcesos[a.nombre?.trim?.()];
      const abrevB = abreviaturasProcesos[b.nombre?.trim?.()];
      const indexA = ordenDeseado.indexOf(abrevA);
      const indexB = ordenDeseado.indexOf(abrevB);

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  }, [resultadosProceso]);

  const data = {
    labels: procesosOrdenados.map(
      (p) => abreviaturasProcesos[p.nombre?.trim?.()] || p.nombre?.trim?.()
    ),
    datasets: [
      {
        label: "Nivel de Capacidad",
        data: procesosOrdenados.map((p) => p.nivelCapacidad),
        backgroundColor: C.blue,
        borderRadius: 6,
        borderSkipped: false,
        barPercentage: 0.55,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 8, left: 4, right: 4, bottom: 0 },
    },
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: C.mode === "dark" ? "#020617" : "#111827",
        padding: 10,
        cornerRadius: 10,
        displayColors: false,
        titleFont: { size: 12, weight: 700, family: FONT_FAMILY },
        bodyFont: { size: 12, family: FONT_FAMILY },
        callbacks: {
          title: (tooltipItems) => procesosOrdenados[tooltipItems[0].dataIndex]?.nombre,
          label: (context) => `Nivel de capacidad: ${context.formattedValue}`,
        },
      },
      datalabels: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: userRole === "ROLE_PREMIUM" ? 2 : 5,
        ticks: {
          stepSize: 1,
          color: C.muted,
          font: { size: 11, weight: 600, family: FONT_FAMILY },
        },
        grid: {
          color: C.mode === "dark" ? "rgba(148,163,184,0.12)" : "rgba(16,24,40,0.07)",
        },
        border: { display: false },
      },
      x: {
        ticks: {
          color: C.muted,
          font: { size: 10.5, weight: 600, family: FONT_FAMILY },
          maxRotation: 0,
        },
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, mt: 2.5 }}>
      <Paper elevation={0} sx={{ ...getPanelSx(C), p: { xs: 2, md: 2.5 } }}>
        <SectionHeader
          C={C}
          eyebrow="Mapa de capacidad"
          title="Nivel obtenido por proceso"
          description="Comparativa de los niveles de capacidad alcanzados en cada proceso evaluado."
          action={
            <Chip
              size="small"
              label="UNE 0080"
              sx={{
                height: 24,
                fontSize: "0.68rem",
                fontWeight: 800,
                color: C.blue,
                bgcolor: alpha(C.blue, C.mode === "dark" ? 0.16 : 0.07),
                border: `1px solid ${alpha(C.blue, 0.18)}`,
                fontFamily: FONT_FAMILY,
              }}
            />
          }
        />

        <Box sx={{ height: { xs: 360, md: 390 }, mt: 1.5 }}>
          <Bar options={options} data={data} plugins={[ChartDataLabels]} />
        </Box>
      </Paper>
    </Box>
  );
};

const RadarDomainCard = ({ title, subtitle, chip, accent, icon, children, refBox, C }) => (
  <Paper
    elevation={0}
    sx={{
      ...getPanelSx(C),
      height: "100%",
      p: 1.9,
      transition: "all 200ms ease",

      "&:hover": {
        transform: { xs: "none", md: "translateY(-3px)" },
        boxShadow: C.hoverShadow,
        borderColor: alpha(accent, 0.34),
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1.15} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "14px",
            bgcolor: alpha(accent, C.mode === "dark" ? 0.16 : 0.08),
            color: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1px solid ${alpha(accent, 0.18)}`,
            "& svg": { fontSize: 20 },
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography
            sx={{
              color: C.heading,
              fontWeight: 850,
              fontSize: "0.8rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ color: C.muted, fontSize: "0.7rem", fontFamily: FONT_FAMILY }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>

      <Chip
        label={chip}
        size="small"
        sx={{
          height: 22,
          fontSize: "0.64rem",
          bgcolor: C.cardSoft,
          color: C.text,
          fontWeight: 800,
          border: `1px solid ${C.borderSoft}`,
          fontFamily: FONT_FAMILY,
        }}
      />
    </Stack>

    <Box
      sx={{
        p: 1,
        borderRadius: "16px",
        bgcolor: C.cardSoft,
        border: `1px solid ${C.borderSoft}`,
      }}
    >
      <Typography
        sx={{
          color: C.muted,
          fontWeight: 750,
          fontSize: "0.68rem",
          px: 0.5,
          fontFamily: FONT_FAMILY,
        }}
      >
        Nivel de capacidad
      </Typography>

      <Box ref={refBox} sx={{ height: 245, mt: 0.5 }}>
        {children}
      </Box>
    </Box>
  </Paper>
);

const ActionCard = ({ icon, title, description, buttonLabel, onClick, outlined = false, C }) => (
  <Paper
    elevation={0}
    sx={{
      ...getPanelSx(C),
      p: 2.3,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      transition: "all 200ms ease",

      "&:hover": {
        transform: { xs: "none", md: "translateY(-3px)" },
        boxShadow: C.hoverShadow,
        borderColor: C.borderStrong,
      },
    }}
  >
    <Stack spacing={1.6}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "15px",
          bgcolor: alpha(C.blue, C.mode === "dark" ? 0.16 : 0.08),
          color: C.blue,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${alpha(C.blue, 0.18)}`,
          "& svg": { fontSize: 22 },
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          sx={{
            fontWeight: 850,
            fontSize: "0.9rem",
            color: C.heading,
            fontFamily: FONT_FAMILY,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: C.muted,
            mt: 0.5,
            lineHeight: 1.6,
            fontSize: "0.76rem",
            fontFamily: FONT_FAMILY,
          }}
        >
          {description}
        </Typography>
      </Box>
    </Stack>

    <Button
      fullWidth
      variant={outlined ? "outlined" : "contained"}
      endIcon={<ArrowForwardIcon sx={{ fontSize: 17 }} />}
      onClick={onClick}
      sx={{
        mt: 2.3,
        borderRadius: "13px",
        py: 0.9,
        textTransform: "none",
        fontSize: "0.76rem",
        fontWeight: 800,
        boxShadow: "none",
        fontFamily: FONT_FAMILY,

        ...(outlined
          ? {
              color: C.blue,
              borderColor: alpha(C.blue, 0.35),
              "&:hover": {
                borderColor: C.blue,
                bgcolor: alpha(C.blue, 0.06),
                boxShadow: "none",
              },
            }
          : {
              bgcolor: C.blue,
              "&:hover": {
                bgcolor: C.blueDark,
                boxShadow: "none",
              },
            }),
      }}
    >
      {buttonLabel}
    </Button>
  </Paper>
);

const DashboardResultados = () => {
  const { cuestionarioId } = useParams();
  const navigate = useNavigate();

  const themeMode = "light";
  const C = useMemo(() => getTheme("light"), []);

  const [resultados, setResultados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [tareas, setTareas] = useState([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [capturedChartImages, setCapturedChartImages] = useState(null);

  const radarGobiernoRef = useRef(null);
  const radarGestionRef = useRef(null);
  const radarCalidadRef = useRef(null);

  useEffect(() => {
    document.body.style.background = C.page;
  }, [C.page]);

  useEffect(() => {
    if (!isGeneratingPdf) return;

    const captureAndGenerate = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        const options = {
          pixelRatio: 2,
          backgroundColor: "#ffffff",
        };

        const captureNode = async (ref) => {
          if (!ref.current) throw new Error("La referencia al gráfico no está disponible.");

          const dataUrl = await htmlToImage.toPng(ref.current, options);

          return {
            dataUrl,
            width: ref.current.clientWidth,
            height: ref.current.clientHeight,
          };
        };

        const [gobiernoData, gestionData, calidadData] = await Promise.all([
          captureNode(radarGobiernoRef),
          captureNode(radarGestionRef),
          captureNode(radarCalidadRef),
        ]);

        setCapturedChartImages({
          gobierno: gobiernoData,
          gestion: gestionData,
          calidad: calidadData,
        });
      } catch (error) {
        console.error("Error al capturar los gráficos:", error);
        setPdfError("No se pudieron generar las imágenes de los gráficos.");
        setIsGeneratingPdf(false);
      }
    };

    captureAndGenerate();
  }, [isGeneratingPdf]);

  useEffect(() => {
    const token = getAuthToken();

    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      setUserRole(decodedToken.role);
    } catch (error) {
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      setLoading(true);

      try {
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
        const endpointResultados = API_ENDPOINTS.resultadosDetallados(cuestionarioId);

        const [resultadosResponse, tareasResponse] = await Promise.all([
          axiosInstance.get(endpointResultados, authHeaders),
          axiosInstance.get(API_ENDPOINTS.todasLasTareas(cuestionarioId), authHeaders),
        ]);

        const resultadosData = resultadosResponse.data;
        const tareasData = tareasResponse.data;

        const generalResultsData = [];

        if (resultadosData.preguntasGenerales && typeof resultadosData.preguntasGenerales === "object") {
          Object.values(resultadosData.preguntasGenerales).forEach((nivel) => {
            if (nivel.preguntas?.length > 0) {
              generalResultsData.push(...nivel.preguntas);
            }
          });
        }

        setResultados({
          ...resultadosData,
          resultadosGenerales: generalResultsData,
        });

        setTareas(tareasData || []);
      } catch (err) {
        console.error("Error al cargar los datos de la página:", err);
        setResultados(null);
        setTareas([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [cuestionarioId, navigate]);

  const totalesPorTipo = useMemo(() => {
    const rows = resultados?.resultadosPorProceso || [];

    return {
      gestion: rows.filter((r) => r.tipo === "Gestion").length,
      gobierno: rows.filter((r) => r.tipo === "Gobierno").length,
      calidad: rows.filter((r) => r.tipo === "Calidad").length,
      total: rows.length,
    };
  }, [resultados]);

  const maturity = Number(resultados?.nivelMadurez || 0);
  const maturityPercent = Math.min(100, Math.max(0, (maturity / 5) * 100));

  const formattedDate = resultados?.fecha
    ? new Date(resultados.fecha).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "No disponible";

  const toggleThemeMode = () => {};

  if (loading) return <DashboardSkeleton C={C} />;

  if (!resultados) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: C.background,
          display: "grid",
          placeItems: "center",
          p: 3,
          fontFamily: FONT_FAMILY,
        }}
      >
        <Paper elevation={0} sx={{ ...getPanelSx(C), p: 3, maxWidth: 520, textAlign: "center" }}>
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            No se pudieron cargar los resultados. Inténtalo de nuevo más tarde.
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        color: C.text,
        pb: 5,
        background: C.background,
        fontFamily: FONT_FAMILY,
        transition: "background 220ms ease, color 220ms ease",
      }}
    >
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 } }}>
        <Paper
          elevation={0}
          sx={{
            ...getPanelSx(C),
            px: { xs: 1.6, md: 2 },
            py: 1.15,
            mb: 2,
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: C.muted }} />}
            aria-label="breadcrumb"
            sx={{
              "& .MuiBreadcrumbs-li": {
                fontSize: "0.76rem",
                fontFamily: FONT_FAMILY,
              },
            }}
          >
            <MuiLink
              component={RouterLink}
              to="/mis-autoevaluaciones"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: C.blue,
                fontWeight: 800,
                fontSize: "0.76rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
              Dashboard
            </MuiLink>

            <Typography
              sx={{
                color: C.muted,
                fontWeight: 700,
                fontSize: "0.76rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              Resultados
            </Typography>
          </Breadcrumbs>

          <Stack direction="row" spacing={1} alignItems="center">

            {userRole === "ROLE_PREMIUMPRO" && (
              <ClassicButton
                C={C}
                startIcon={
                  isGeneratingPdf ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 18 }} />
                  )
                }
                onClick={() => {
                  setIsGeneratingPdf(true);
                  setPdfError(null);
                }}
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? "Generando..." : "Generar PDF"}
              </ClassicButton>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...getPanelSx(C),
            p: { xs: 2, md: 3 },
            mb: 2,
            overflow: "hidden",
            position: "relative",

            "&:before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                C.mode === "dark"
                  ? "radial-gradient(circle at 0% 0%, rgba(79,156,249,0.13), transparent 32%), radial-gradient(circle at 100% 0%, rgba(211,125,54,0.10), transparent 30%)"
                  : "radial-gradient(circle at 0% 0%, rgba(30,75,129,0.07), transparent 32%), radial-gradient(circle at 100% 0%, rgba(211,125,54,0.08), transparent 30%)",
              pointerEvents: "none",
            },
          }}
        >
          <Grid container spacing={2.5} alignItems="center" sx={{ position: "relative" }}>
            <Grid item xs={12} md={8}>
              <Chip
                label="Autoevaluación de procesos de datos"
                size="small"
                sx={{
                  height: 24,
                  mb: 1.4,
                  bgcolor: alpha(C.blue, C.mode === "dark" ? 0.16 : 0.07),
                  color: C.blue,
                  border: `1px solid ${alpha(C.blue, 0.18)}`,
                  fontWeight: 800,
                  fontSize: "0.68rem",
                  fontFamily: FONT_FAMILY,
                }}
              />

              <Typography
                sx={{
                  color: C.heading,
                  fontSize: { xs: "1.55rem", md: "2rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.06em",
                  lineHeight: 1.08,
                  maxWidth: 820,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Resultados de la autoevaluación de procesos de datos
              </Typography>

              <Typography
                sx={{
                  color: C.muted,
                  mt: 1.1,
                  maxWidth: 760,
                  fontSize: "0.82rem",
                  lineHeight: 1.65,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Los resultados de esta autoevaluación ofrecen una perspectiva inicial basada en tus respuestas. Para obtener una evaluación más precisa sobre el nivel de madurez organizacional y capacidades específicas, te sugerimos someterte a una evaluación formal en el marco de las especificaciones UNE 0077, 0078, 0079 y 0080.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.9,
                  borderRadius: "18px",
                  bgcolor: C.cardSoft,
                  border: `1px solid ${C.borderSoft}`,
                }}
              >
                <Typography
                  sx={{
                    color: C.muted,
                    fontSize: "0.66rem",
                    fontWeight: 850,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Nivel de madurez
                </Typography>

                <Typography
                  sx={{
                    color: C.heading,
                    fontSize: "2rem",
                    fontWeight: 950,
                    lineHeight: 1,
                    mt: 1.2,
                    letterSpacing: "-0.05em",
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {userRole === "ROLE_FREEMIUM" || resultados.nivelMadurez === -1
                    ? "—"
                    : `${Math.round(maturity)}/5`}
                </Typography>

                <Typography sx={{ color: C.muted, fontSize: "0.72rem", mt: 0.8, fontFamily: FONT_FAMILY }}>
                  Madurez organizacional
                </Typography>

                <Box
                  sx={{
                    mt: 1.7,
                    height: 7,
                    borderRadius: 99,
                    bgcolor: alpha(C.blue, 0.12),
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${maturityPercent}%`,
                      height: "100%",
                      bgcolor: C.blue,
                      borderRadius: 99,
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <StatCard icon={<EmojiEventsIcon />} label="Nivel" accent={C.blue} C={C}>
              {userRole === "ROLE_FREEMIUM" ? (
                <Stack spacing={0.7}>
                  <UpgradeIcon sx={{ fontSize: 24, color: C.blue }} />
                  <Typography sx={{ fontWeight: 850, fontSize: "0.8rem", color: C.text, fontFamily: FONT_FAMILY }}>
                    Análisis de madurez
                  </Typography>
                  <Typography sx={{ color: C.muted, fontSize: "0.7rem", fontFamily: FONT_FAMILY }}>
                    Disponible en planes superiores.
                  </Typography>
                  <MuiLink
                    href="mailto:dqpa@dqteam.es"
                    underline="hover"
                    sx={{ color: C.blue, fontWeight: 800, fontSize: "0.72rem", fontFamily: FONT_FAMILY }}
                  >
                    dqpa@dqteam.es
                  </MuiLink>
                </Stack>
              ) : resultados.nivelMadurez === -1 ? (
                <Stack spacing={0.7}>
                  <Typography sx={{ color: C.blue, fontWeight: 850, fontSize: "0.86rem", fontFamily: FONT_FAMILY }}>
                    No disponible
                  </Typography>
                  <Typography sx={{ color: C.muted, fontSize: "0.7rem", fontFamily: FONT_FAMILY }}>
                    Es necesario evaluar todos los procesos correspondientes al Nivel 1.
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Typography sx={{ color: C.heading, fontSize: "1.55rem", fontWeight: 950, lineHeight: 1, fontFamily: FONT_FAMILY }}>
                    {Math.round(maturity)}/5
                  </Typography>
                  <Typography sx={{ color: C.text, fontWeight: 780, fontSize: "0.78rem", mt: 0.75, fontFamily: FONT_FAMILY }}>
                    Madurez organizacional
                  </Typography>
                  <Box sx={{ mt: 1.4, height: 7, borderRadius: 99, bgcolor: alpha(C.blue, 0.12), overflow: "hidden" }}>
                    <Box
                      sx={{
                        width: `${maturityPercent}%`,
                        height: "100%",
                        bgcolor: C.blue,
                        borderRadius: 99,
                      }}
                    />
                  </Box>
                </>
              )}
            </StatCard>
          </Grid>

          <Grid item xs={12} md={3}>
            <StatCard
              C={C}
              icon={<ChecklistIcon />}
              label="Tareas"
              accent={C.orange}
              value={tareas.length}
              title="Tareas de mejora"
              subtitle="Identificadas para optimización"
              footer={
                tareas.length > 0 ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 17 }} />}
                    onClick={() => navigate(`/propuestas-mejora/${cuestionarioId}`)}
                    sx={{
                      borderRadius: "12px",
                      borderColor: alpha(C.orange, 0.35),
                      color: C.orange,
                      textTransform: "none",
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      py: 0.7,
                      fontFamily: FONT_FAMILY,
                      "&:hover": {
                        borderColor: C.orange,
                        bgcolor: alpha(C.orange, 0.06),
                      },
                    }}
                  >
                    Ver plan de mejora
                  </Button>
                ) : (
                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ color: C.success }}>
                    <CheckCircleIcon fontSize="small" />
                    <Typography sx={{ fontWeight: 800, fontSize: "0.72rem", fontFamily: FONT_FAMILY }}>
                      Sin tareas pendientes
                    </Typography>
                  </Stack>
                )
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <StatCard
              C={C}
              icon={<BarChartIcon />}
              label="Procesos"
              accent={C.slate}
              value={totalesPorTipo.total}
              title="Procesos evaluados"
              subtitle="Gobierno, gestión y calidad"
              footer={
                <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                  {[
                    `Gobierno ${totalesPorTipo.gobierno}`,
                    `Gestión ${totalesPorTipo.gestion}`,
                    `Calidad ${totalesPorTipo.calidad}`,
                  ].map((label) => (
                    <Chip
                      key={label}
                      size="small"
                      label={label}
                      sx={{
                        height: 22,
                        fontSize: "0.66rem",
                        fontWeight: 750,
                        bgcolor: C.cardSoft,
                        color: C.text,
                        border: `1px solid ${C.borderSoft}`,
                        fontFamily: FONT_FAMILY,
                      }}
                    />
                  ))}
                </Stack>
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <StatCard
              C={C}
              icon={<CalendarMonthIcon />}
              label="Fecha"
              accent={C.success}
              value={formattedDate}
              title="Fecha de evaluación"
              subtitle="Autoevaluación integral"
              footer={
                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ color: C.success }}>
                  <TaskAltIcon fontSize="small" />
                  <Typography sx={{ fontWeight: 800, fontSize: "0.72rem", fontFamily: FONT_FAMILY }}>
                    Completada
                  </Typography>
                </Stack>
              }
            />
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ ...getPanelSx(C), p: 1.8, mb: 2.5 }}>
          <Stack direction="row" spacing={1.3} alignItems="flex-start">
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "13px",
                bgcolor: alpha(C.blue, C.mode === "dark" ? 0.16 : 0.07),
                color: C.blue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                border: `1px solid ${alpha(C.blue, 0.16)}`,
                "& svg": { fontSize: 18 },
              }}
            >
              <InfoIcon />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 850, color: C.heading, fontSize: "0.8rem", mb: 0.3, fontFamily: FONT_FAMILY }}>
                Procesos evaluados
              </Typography>
              <Typography sx={{ color: C.muted, lineHeight: 1.6, fontSize: "0.76rem", fontFamily: FONT_FAMILY }}>
                Se presentan los procesos definidos en las especificaciones UNE 0077, 0078 y 0079 para ofrecer
                una visión global del alcance de la autoevaluación.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Box sx={{ mb: 2.5 }}>
          <SectionHeader
            C={C}
            eyebrow="Dominios UNE"
            title="Radar de capacidad por ámbito"
            description="Vista comparativa del desempeño por gobierno, gestión y calidad del dato."
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <RadarDomainCard
                C={C}
                title="Gobierno del Dato"
                subtitle="Estrategia, políticas, valor y riesgos"
                chip="UNE 0077"
                accent={C.blue}
                icon={<RadarIcon />}
                refBox={radarGobiernoRef}
              >
                <RadarChartComponent
                  tipoKey="Gobierno"
                  procesos={procesosPorTipo.Gobierno}
                  resultados={resultados.resultadosPorProceso}
                  userRole={userRole}
                />
              </RadarDomainCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <RadarDomainCard
                C={C}
                title="Gestión del Dato"
                subtitle="Operación, arquitectura y seguridad"
                chip="UNE 0078"
                accent={C.orange}
                icon={<InsightsIcon />}
                refBox={radarGestionRef}
              >
                <RadarChartComponent
                  tipoKey="Gestion"
                  procesos={procesosPorTipo.Gestion}
                  resultados={resultados.resultadosPorProceso}
                  userRole={userRole}
                />
              </RadarDomainCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <RadarDomainCard
                C={C}
                title="Calidad del Dato"
                subtitle="Planificación, control y mejora"
                chip="UNE 0079"
                accent={C.slate}
                icon={<WorkspacePremiumIcon />}
                refBox={radarCalidadRef}
              >
                <RadarChartComponent
                  tipoKey="Calidad"
                  procesos={procesosPorTipo.Calidad}
                  resultados={resultados.resultadosPorProceso}
                  userRole={userRole}
                />
              </RadarDomainCard>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {(userRole === "ROLE_PREMIUM" || userRole === "ROLE_PREMIUMPRO") &&
        resultados?.resultadosPorProceso?.length > 0 && (
          <BarChartCapacidad
            C={C}
            resultadosProceso={resultados.resultadosPorProceso}
            userRole={userRole}
          />
        )}

      <Box sx={{ px: { xs: 2, md: 3 }, mt: 2.5 }}>
        {userRole === "ROLE_FREEMIUM" || userRole === "ROLE_PREMIUM" ? (
          resultados && (
            <DetallePorProceso
              userRole={userRole}
              resultadosPorProceso={resultados.resultadosPorProceso || []}
              resultadosGenerales={resultados.resultadosGenerales || []}
            />
          )
        ) : (
          <Box>
            <SectionHeader
              C={C}
              eyebrow="Acciones recomendadas"
              title="Continúa con el análisis avanzado"
              description="Accede al detalle completo o consulta el plan de mejora para priorizar las siguientes acciones."
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ActionCard
                  C={C}
                  icon={<AssessmentIcon />}
                  title="Evaluación detallada"
                  description="Consulta los resultados detallados de la autoevaluación, incluyendo respuestas y niveles por proceso."
                  buttonLabel="Ver evaluación completa"
                  onClick={() => navigate(`/detalle-evaluacion/${cuestionarioId}`)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ActionCard
                  C={C}
                  icon={<ChecklistIcon />}
                  title="Plan de mejora"
                  description="Visualiza recomendaciones y tareas accionables para avanzar en madurez y capacidad."
                  buttonLabel="Ver plan de mejora"
                  outlined
                  onClick={() => navigate(`/propuestas-mejora/${cuestionarioId}`)}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {isGeneratingPdf && capturedChartImages && (
          <GeneradorPDF
            cuestionarioId={cuestionarioId}
            chartImages={capturedChartImages}
            onComplete={() => {
              setIsGeneratingPdf(false);
              setCapturedChartImages(null);
            }}
            onError={(error) => {
              console.error("Error en PDF recibido por el padre:", error.message);
              setPdfError("No se pudo generar el informe. Por favor, inténtelo de nuevo.");
              setIsGeneratingPdf(false);
              setCapturedChartImages(null);
            }}
          />
        )}

        {pdfError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>
            {pdfError}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default DashboardResultados;
